// Copyright ©️ 2026 eVolve MEP, LLC

import type { HexTriplet } from '@evolvemep/api-ts';
import * as THREE from 'three';

import { isNil, isNotNil } from 'helpers/isNotNil';
import {
  HighlightableGroup,
  type HighlightableObjectName,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/HighlightableGroup';
import { labelTheming } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/labelTheming';

type LabelCanvasOpts = {
  fontSize: number;
  fontColor: HexTriplet;
  fillColor: HexTriplet;
  strokeThickness: number;
  strokeColor: HexTriplet;
};

const PLAIN_LABEL_OPTS = {
  fontSize: 128,
  fontColor: labelTheming.colors.black,
  fillColor: labelTheming.colors.background,
  strokeThickness: 10,
  strokeColor: labelTheming.colors.backgroundDark,
} as const satisfies LabelCanvasOpts;

const HIGHLIGHTED_LABEL_OPTS = {
  fontSize: 128,
  fontColor: labelTheming.colors.white,
  fillColor: labelTheming.colors.primary,
  strokeThickness: 10,
  strokeColor: labelTheming.colors.primary,
} as const satisfies LabelCanvasOpts;

export type LabelText = {
  description: string;
  measurement?: string;
};
/**
 * Helper method to create a canvas texture for a label.
 * Mutates the canvas.
 */
const writeLabelToCanvas = (
  text: LabelText,
  canvas: OffscreenCanvas,
  { fontSize, fontColor, fillColor, strokeThickness, strokeColor }: LabelCanvasOpts,
) => {
  const ctx = canvas.getContext('2d');
  if (isNil(ctx)) throw new Error('Could not get context');
  const paddingX = Math.ceil(fontSize * 0.4);
  const paddingY = Math.ceil(fontSize * 0.2);
  const descriptionFont = `${fontSize}px IBM Plex Sans`;
  ctx.font = descriptionFont;
  const descriptionWidth = ctx.measureText(text.description).width;
  const measurementFont = `600 ${fontSize}px IBM Plex Sans`;
  ctx.font = measurementFont;
  const measurementWidth = isNotNil(text.measurement) ? ctx.measureText(text.measurement).width + paddingX : 0;

  const textWidth = descriptionWidth + measurementWidth;
  const textHeight = Math.ceil(fontSize * 1.25);

  // oxlint-disable-next-line evolve/no-param-reassign
  canvas.width = textWidth + paddingX * 2;
  // oxlint-disable-next-line evolve/no-param-reassign
  canvas.height = textHeight + paddingY * 2;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Background
  const radius = canvas.height * 0.1;
  ctx.fillStyle = fillColor;
  ctx.beginPath();
  ctx.roundRect(
    strokeThickness / 2,
    strokeThickness / 2,
    canvas.width - strokeThickness,
    canvas.height - strokeThickness,
    radius,
  );
  ctx.fill();
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = strokeThickness;
  ctx.stroke();

  // Text
  ctx.fillStyle = fontColor;
  ctx.font = descriptionFont;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText(text.description, paddingX, canvas.height / 2);
  if (isNotNil(text.measurement)) {
    ctx.font = measurementFont;
    ctx.fillText(text.measurement, paddingX * 2 + descriptionWidth, canvas.height / 2);
  }
};

export class LabelBillboardGeometry extends HighlightableGroup {
  labelCanvas: OffscreenCanvas;
  canvasTexture: THREE.CanvasTexture<OffscreenCanvas>;
  textMaterial: THREE.SpriteMaterial;
  textBillboard: THREE.Sprite;
  constructor(
    highlightableObjectName: HighlightableObjectName,
    private text: LabelText,
  ) {
    super(highlightableObjectName);

    this.labelCanvas = new OffscreenCanvas(1, 1);
    writeLabelToCanvas(this.text, this.labelCanvas, PLAIN_LABEL_OPTS);
    this.canvasTexture = new THREE.CanvasTexture(this.labelCanvas);

    // The OffscreenCanvas always writes sRGB bytes. Tell THREE.js so it decodes
    // sRGB to linear when sampling; the renderer's linear to sRGB output pass then
    // reproduces the exact palette colours instead of gamma-brightening them.
    this.canvasTexture.colorSpace = THREE.SRGBColorSpace;
    this.canvasTexture.minFilter = THREE.LinearFilter;
    this.canvasTexture.magFilter = THREE.LinearFilter;
    this.canvasTexture.needsUpdate = true;

    this.textMaterial = new THREE.SpriteMaterial({
      map: this.canvasTexture,
      depthTest: false,
      side: THREE.DoubleSide,
    });

    this.textBillboard = new THREE.Sprite(this.textMaterial);
    this.activateHitDetection(this.textBillboard);

    // Render above other geometry
    this.textBillboard.renderOrder = labelTheming.renderOrder.labelBillboard;
    // Adjust the scale to maintain the same aspect ratio as the canvas.
    // Without this, the sprite would look "squished" or "stretched".
    const aspect = this.labelCanvas.width / this.labelCanvas.height;
    this.scale.set(aspect, 1, 1);

    this.add(this.textBillboard);
  }

  private updateCanvas(isHighlighted: boolean) {
    // Skip the label change if it's already highlighted or unhighlighted
    if (isHighlighted === this.isHighlighted) return;
    writeLabelToCanvas(this.text, this.labelCanvas, isHighlighted ? HIGHLIGHTED_LABEL_OPTS : PLAIN_LABEL_OPTS);
    this.canvasTexture.needsUpdate = true;
    // Reapply the aspect ratio.
    const aspect = this.labelCanvas.width / this.labelCanvas.height;
    this.scale.set(aspect, 1, 1);
  }

  highlight() {
    this.updateCanvas(true);
    super.highlight();
  }

  unhighlight() {
    this.updateCanvas(false);
    super.unhighlight();
  }

  dispose() {
    this.textMaterial.map?.dispose();
    super.dispose();
  }
}
